import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Alert,
    Typography
} from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { DialogTheme } from "../styles/DialogTheme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { enUS } from "date-fns/locale";
import {
    getFullTimeZoneName,
    getFullTimeZoneNameForTimeZone
} from "../modules/TimeService";
import { companyMasterTz } from "../atoms/atoms";
import { useAtom } from "jotai";
import CustomTimePicker from "../user/components/CustomTimePicker";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const DateTimeSelector = ({
    open,
    onClose,
    onChange,
    initialDateTime = new Date(),
    title = "Custom date and time",
    timezone = null,
    handleBackToSuggestedTimes = () => {},
    showBackToSuggestedTimes = false
}) => {
    const theme = useTheme();
    const [selectedDate, setSelectedDate] = useState(initialDateTime);
    const [selectedTime, setSelectedTime] = useState(initialDateTime);
    const [openDate, setOpenDate] = useState(false);
    const [openTime, setOpenTime] = useState(false);

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            // Update time to match the selected date
            const newDateTime = new Date(date);
            newDateTime.setHours(selectedTime.getHours());
            newDateTime.setMinutes(selectedTime.getMinutes());
            setSelectedTime(newDateTime);
        }
    };

    const handleTimeChange = (time) => {
        if (time) {
            const timeObj = new Date(time);
            setSelectedTime(timeObj);
            // Update date to match the selected time
            const newDateTime = new Date(selectedDate);
            newDateTime.setHours(timeObj.getHours());
            newDateTime.setMinutes(timeObj.getMinutes());
            setSelectedDate(newDateTime);
        }
    };

    const handleConfirm = () => {
        // Combine date and time into a single Date object
        const combinedDateTime = new Date(selectedDate);
        combinedDateTime.setHours(selectedTime.getHours());
        combinedDateTime.setMinutes(selectedTime.getMinutes());
        combinedDateTime.setSeconds(0);
        combinedDateTime.setMilliseconds(0);

        onChange(combinedDateTime);
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <ThemeProvider theme={DialogTheme}>
            <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    {showBackToSuggestedTimes ? (
                        <Button
                            variant="text"
                            id="add-sessions-button"
                            color="primary"
                            sx={{
                                marginBottom: "18px",
                                color: theme.palette.info.main,
                                textTransform: "none",
                                padding: "0px",
                                height: "28px",
                                fontSize: "14px"
                            }}
                            onClick={handleBackToSuggestedTimes}
                            startIcon={
                                <ArrowBackIcon
                                    size="medium"
                                    sx={{
                                        marginRight: "8px",
                                        transform: "translate(0px, -1px)"
                                    }}
                                />
                            }
                        >
                            Back to time suggestions
                        </Button>
                    ) : (
                        <></>
                    )}
                    {timezone && (
                        <Alert
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "10px 0px",
                                border: `1px solid ${theme.palette.info.main}`,
                                marginBottom: "0px",
                                backgroundColor: theme.palette.info.lightest,
                                textAlign: "center"
                            }}
                            icon={false}
                        >
                            <Typography
                                color={theme.palette.info.main}
                                fontWeight={500}
                                display={"inline"}
                            >
                                Times shown in{" "}
                                {getFullTimeZoneNameForTimeZone(
                                    initialDateTime,
                                    timezone
                                ) || ""}
                            </Typography>
                        </Alert>
                    )}
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={enUS}
                    >
                        <Grid container spacing={2.25}>
                            <Grid item xs={12} sm={6}>
                                <DatePicker
                                    label="Date"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    open={openDate}
                                    onOpen={() => setOpenDate(true)}
                                    onClose={() => {
                                        setOpenDate(false);
                                    }}
                                    inputFormat="MMM dd, yyyy"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            inputProps={{
                                                ...params.inputProps,
                                                readOnly: true
                                            }}
                                            sx={{
                                                "& .MuiInputAdornment-root": {
                                                    display: "none"
                                                }
                                            }}
                                            onClick={(e) => {
                                                setOpenDate(true);
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTimePicker
                                    id="start-time"
                                    name={`timeBlocks.startTime`}
                                    type="startTime"
                                    value={selectedTime}
                                    onChange={handleTimeChange}
                                    outlinedInputSx={{
                                        padding: "7.5px 14px",
                                        color: theme.palette.text.main
                                    }}
                                    step={5}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions sx={{ gap: "10px" }}>
                    <Button
                        onClick={handleCancel}
                        color="primary"
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        color="primary"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
};

export default DateTimeSelector;
